import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Button, Heading, Badge, Text, VStack, HStack, Textarea, Spinner, useToast,
  Grid, GridItem, Radio, RadioGroup, FormControl, FormLabel, Flex, IconButton, Checkbox
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const ReviewSubmissionForm = ({ caseId, aiResultId, onReviewSubmitted }) => {
  const [reviewOutcome, setReviewOutcome] = useState('');
  const [noteText, setNoteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const handleSubmit = async () => {
    if (!reviewOutcome) {
      toast({ title: "Please select a review outcome", status: "warning", duration: 3000 });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await callApi('/api/manual-coding/review', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          review_outcome: reviewOutcome,
          note_text: noteText,
          ai_result_id: aiResultId
        }),
      });
      const newNote = await response.json();
      if (response.ok) {
        toast({ title: "Review submitted successfully", status: "success", duration: 2000 });
        setReviewOutcome('');
        setNoteText('');
        onReviewSubmitted(newNote);
      } else {
        throw new Error(newNote.error || 'Failed to submit review');
      }
    } catch (error) {
      toast({ title: "Error submitting review", description: error.message, status: "error", duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box mt={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
      <FormControl as="fieldset">
        <FormLabel as="legend" fontWeight="bold">Submit Review</FormLabel>
        <RadioGroup onChange={setReviewOutcome} value={reviewOutcome}>
          <HStack spacing="24px">
            <Radio value="revedy_correct">Revedy is correct</Radio>
            <Radio value="manual_correct">Manual Coding is correct</Radio>
          </HStack>
        </RadioGroup>
      </FormControl>
      <Textarea
        placeholder="Add an optional note..."
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        mt={4}
        mb={2}
        bg="white"
      />
      <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting} width="full">
        Submit Review & Next
      </Button>
    </Box>
  );
};

const ManualCPTReviewPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);

  // Navigation Filters
  const [skipReviewed, setSkipReviewed] = useState(true);
  const [skipMatches, setSkipMatches] = useState(true);
  
  // Inherit match mode from dashboard for consistent navigation
  const matchMode = location.state?.matchMode || 'exact';

  // Get the list of cases from navigation state to enable Next/Prev
  // We use state here so we can update statuses locally as we review
  const [caseList, setCaseList] = useState(location.state?.caseList || []);
  
  const currentIndex = useMemo(() => 
    caseList.findIndex(c => c.case_id === caseId), 
  [caseList, caseId]);

  const replacePromptWithGuidance = (text) => {
    if (typeof text !== 'string') {
      return text;
    }
    return text.replace(/prompt's|prompts|prompt/gi, 'guidance');
  };

  const fetchCaseDetails = async (id) => {
    setLoading(true);
    setPdfUrl(null); // Reset PDF when switching cases
    try {
      let url = `/api/manual-coding/case/${id}`;
      if (location.state?.configId) {
        url += `?config_id=${location.state.configId}`;
      }
      const response = await callApi(url, accessToken);
      const data = await response.json();
      if (response.ok) {
        setCaseData(data);
        // Automatically load the first file if available
        if (data.files && data.files.length > 0) {
            handleFileView(data.files[0], id);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch case details');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleFileView = async (file, currentCaseId) => {
    setFileLoading(true);
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: currentCaseId || caseId,
          file_id: file.id,
          disposition: 'inline'
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open file');
      }
    } catch (error) {
      toast({
        title: 'Failed to load file',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setFileLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && caseId) {
      fetchCaseDetails(caseId);
    }
    // Cleanup object URL on unmount or change
    return () => {
        if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
  }, [accessToken, caseId]);

  const findNextEligibleIndex = (startIndex, direction) => {
    let nextIndex = startIndex + direction;
    
    while (nextIndex >= 0 && nextIndex < caseList.length) {
      const candidate = caseList[nextIndex];
      
      const isReviewed = candidate.status === 'Reviewed';
      const currentStatusKey = `match_status_${matchMode}`;
      const status = candidate[currentStatusKey];
      const isMatch = status === 'Match' || status === 'Level Match';

      // Check if we should skip this candidate
      const shouldSkip = (skipReviewed && isReviewed) || (skipMatches && isMatch);

      if (!shouldSkip) {
        return nextIndex;
      }
      
      nextIndex += direction;
    }
    return -1;
  };

  const handleNavigate = (direction) => {
    if (currentIndex === -1) return;
    
    const nextIndex = findNextEligibleIndex(currentIndex, direction);
    
    if (nextIndex !== -1) {
      const nextCase = caseList[nextIndex];
      navigate(`/cpt-feedback-review/${nextCase.case_id}`, { 
        state: { 
          caseList,
          configId: location.state?.configId 
        } 
      });
    } else {
      toast({ 
        title: direction > 0 ? "No more cases found" : "No previous cases found", 
        description: "Based on current filters (Skip Reviewed/Matches)",
        status: "info", 
        duration: 2000 
      });
    }
  };

  const handleReviewSubmitted = (newNote) => {
    // Update local state to reflect the new note/status immediately
    setCaseData(prev => ({
        ...prev,
        status: 'Reviewed',
        notes: [...(prev.notes || []), newNote]
    }));

    // Update the caseList state so navigation knows this is now reviewed
    setCaseList(prevList => {
      const newList = [...prevList];
      if (currentIndex !== -1) {
        newList[currentIndex] = { ...newList[currentIndex], status: 'Reviewed' };
      }
      return newList;
    });
    
    // We need to wait for the state update to propagate or just pass the updated list logic
    // Since setCaseList is async, we can't rely on 'caseList' being updated in this scope immediately for navigation
    // However, findNextEligibleIndex uses 'caseList' from closure. 
    // To be safe, we can manually check the next index assuming the current one is now 'Reviewed'
    
    // Actually, since we are just moving forward, and the current one is now reviewed,
    // if 'skipReviewed' is true, the next call to handleNavigate(1) will naturally skip the current index 
    // (because we start searching from currentIndex + 1).
    
    // Trigger navigation after a brief delay to allow UI to update
    setTimeout(() => {
        // We call findNextEligibleIndex relative to the CURRENT index.
        // Since we just reviewed the current index, we want to find the next one.
        // We don't need to pass the updated list because findNextEligibleIndex looks at indices *after* current.
        handleNavigate(1);
    }, 100);
  };

  const OutcomeBadge = ({ outcome }) => {
    if (!outcome) return null;
    const color = outcome === 'revedy_correct' ? 'green' : 'purple';
    const text = outcome === 'revedy_correct' ? 'Revedy Correct' : 'Manual Correct';
    return <Badge colorScheme={color} ml={2}>{text}</Badge>;
  };

  if (loading) {
    return (
        <Box>
            <HeaderMenus />
            <Flex justify="center" align="center" h="80vh">
                <Spinner size="xl" />
            </Flex>
        </Box>
    );
  }

  if (!caseData) return <Box>Case not found</Box>;

  return (
    <Box h="100vh" display="flex" flexDirection="column" overflow="hidden">
      <HeaderMenus />
      
      {/* Toolbar */}
      <Flex p={4} borderBottomWidth="1px" bg="white" align="center" justify="space-between">
        <HStack>
            <Button leftIcon={<ArrowBackIcon />} onClick={() => navigate('/cpt-feedback-dashboard')}>
                Back to Dashboard
            </Button>
            <Heading size="md" ml={4}>Review Case: {caseData.external_case_id}</Heading>
            <Badge colorScheme={caseData.status === 'Reviewed' ? 'green' : 'orange'}>
                {caseData.status}
            </Badge>
        </HStack>
        
        <HStack spacing={6}>
            <HStack spacing={4} borderRightWidth="1px" pr={4} mr={2}>
                <Checkbox 
                    isChecked={skipReviewed} 
                    onChange={(e) => setSkipReviewed(e.target.checked)}
                    size="sm"
                >
                    Skip Reviewed
                </Checkbox>
                <Checkbox 
                    isChecked={skipMatches} 
                    onChange={(e) => setSkipMatches(e.target.checked)}
                    size="sm"
                >
                    Skip Matches
                </Checkbox>
            </HStack>

            <HStack>
                <Button 
                    leftIcon={<ChevronLeftIcon />} 
                    onClick={() => handleNavigate(-1)} 
                    isDisabled={findNextEligibleIndex(currentIndex, -1) === -1}
                >
                    Previous
                </Button>
                <Text fontSize="sm" color="gray.500">
                    {currentIndex + 1} of {caseList.length}
                </Text>
                <Button 
                    rightIcon={<ChevronRightIcon />} 
                    onClick={() => handleNavigate(1)} 
                    isDisabled={findNextEligibleIndex(currentIndex, 1) === -1}
                >
                    Next
                </Button>
            </HStack>
        </HStack>
      </Flex>

      {/* Main Content - Split Screen */}
      <Grid templateColumns="40% 60%" flex="1" overflow="hidden">
        
        {/* Left Panel: Case Details & Form */}
        <GridItem overflowY="auto" p={6} borderRightWidth="1px" bg="white">
            <VStack align="stretch" spacing={6}>
                
                {/* AI Codes */}
                <Box>
                    <HStack justify="space-between" mb={3} align="center">
                        <HStack>
                            <Text fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase">REVEDY:</Text>
                            <Text fontSize="md" fontWeight="bold" color="blue.600">
                                {Object.keys(caseData.ai_results || {}).join(', ') || 'None'}
                            </Text>
                        </HStack>
                        {caseData.manual_codes && caseData.manual_codes.length > 0 && (
                            <HStack>
                                <Text fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase">Manual:</Text>
                                <Text fontSize="md" fontWeight="bold" color="purple.600">
                                    {caseData.manual_codes.join(', ')}
                                </Text>
                            </HStack>
                        )}
                    </HStack>
                    <VStack align="start" spacing={4}>
                        {Object.entries(caseData.ai_results || {}).map(([code, details]) => (
                        <Box key={code} p={4} borderWidth="1px" borderRadius="md" w="100%" bg="gray.50">
                            <Flex justify="space-between" align="center" mb={2}>
                                <Text fontWeight="bold" fontSize="lg">{code}</Text>
                                <Badge colorScheme={details.billable ? 'green' : 'red'}>
                                    {details.billable ? 'Billable' : 'Not Billable'}
                                </Badge>
                            </Flex>
                            <Text fontSize="sm" color="gray.600" mb={3}>{details.long_description}</Text>
                            
                            <Grid templateColumns="auto 1fr" gap={2} fontSize="sm">
                                <Text fontWeight="semibold">Decision:</Text>
                                <Text>{replacePromptWithGuidance(details.decision)}</Text>
                                
                                <Text fontWeight="semibold">Detail:</Text>
                                <Text>{replacePromptWithGuidance(details.detail)}</Text>
                                
                                <Text fontWeight="semibold">Units:</Text>
                                <Text>{details.units}</Text>
                            </Grid>

                            <Box mt={3}>
                                <Text fontWeight="semibold" fontSize="xs" mb={1}>JUSTIFICATION</Text>
                                <Text fontSize="xs" p={2} bg="white" borderRadius="md" borderWidth="1px" whiteSpace="pre-wrap">
                                    {replacePromptWithGuidance(details.justification)}
                                </Text>
                            </Box>
                        </Box>
                        ))}
                    </VStack>
                </Box>

                {/* Manual Codes */}
                <Box>
                    <Heading size="sm" mb={3} textTransform="uppercase" color="gray.500">Manual Codes</Heading>
                    <Flex wrap="wrap" gap={2}>
                        {(caseData.manual_codes || []).map(code => (
                            <Badge key={code} fontSize="md" p={2} borderRadius="md" colorScheme="purple">
                                {code}
                            </Badge>
                        ))}
                    </Flex>
                </Box>

                {/* Notes History */}
                <Box>
                    <Heading size="sm" mb={3} textTransform="uppercase" color="gray.500">History & Notes</Heading>
                    <VStack spacing={3} align="stretch">
                        {(caseData.notes || []).map(note => (
                            <Box key={note.id} p={3} bg="blue.50" borderRadius="md" fontSize="sm">
                                <HStack justify="space-between" mb={1}>
                                    <Text fontWeight="bold" fontSize="xs">{note.user_name}</Text>
                                    <Text fontSize="xs" color="gray.500">{new Date(note.created_at).toLocaleString()}</Text>
                                </HStack>
                                <OutcomeBadge outcome={note.review_outcome} />
                                <Text mt={1}>{note.note_text}</Text>
                            </Box>
                        ))}
                        {(!caseData.notes || caseData.notes.length === 0) && (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No previous notes.</Text>
                        )}
                    </VStack>
                </Box>

                {/* Submission Form */}
                <ReviewSubmissionForm
                    caseId={caseData.case_id}
                    aiResultId={caseData.ai_result_id}
                    onReviewSubmitted={handleReviewSubmitted}
                />

                {/* File List (if multiple files, allow switching) */}
                {caseData.files && caseData.files.length > 1 && (
                    <Box>
                        <Heading size="sm" mb={2}>Documents</Heading>
                        <VStack align="start">
                            {caseData.files.map(file => (
                                <Button 
                                    key={file.id} 
                                    size="sm" 
                                    variant="link" 
                                    onClick={() => handleFileView(file, caseData.case_id)}
                                >
                                    {file.file_name}
                                </Button>
                            ))}
                        </VStack>
                    </Box>
                )}

            </VStack>
        </GridItem>

        {/* Right Panel: PDF Viewer */}
        <GridItem bg="gray.200" h="100%" position="relative">
            {fileLoading ? (
                <Flex justify="center" align="center" h="100%">
                    <VStack>
                        <Spinner size="xl" color="blue.500" />
                        <Text>Loading Document...</Text>
                    </VStack>
                </Flex>
            ) : pdfUrl ? (
                <iframe 
                    src={pdfUrl} 
                    title="Case Document"
                    width="100%" 
                    height="100%" 
                    style={{ border: 'none' }} 
                />
            ) : (
                <Flex justify="center" align="center" h="100%" color="gray.500">
                    <Text>No document selected or available.</Text>
                </Flex>
            )}
        </GridItem>

      </Grid>
    </Box>
  );
};

export default ManualCPTReviewPage;